import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NaturalLanguageCriteriaSessionService} from "../../service/api/natural-language-criteria-session.service";
import {
    ClarificationQuestion,
    ConversationMessage,
    NaturalLanguageSessionResponse,
    NaturalLanguageSessionStartRequest
} from "../../model/dto/api/natural-language-criteria-session.interface";
import {Subscription} from "rxjs";

/**
 * Natural Language Criteria Session Component.
 * F-031: Natural Language Selection Criteria.
 *
 * Provides a conversational interface for building selection criteria using natural language.
 *
 * Acceptance Criteria:
 * - Given natural language input like 'customers in California with contracts expiring in 30 days',
 *   when processed, then corresponding criteria are generated
 * - Given generated criteria, when reviewed, then user can modify and refine
 * - Given ambiguous input, when processed, then clarifying questions are presented
 */
@Component({
    selector: 'ct-natural-language-criteria-session',
    templateUrl: './natural-language-criteria-session.component.html',
    standalone: false
})
export class NaturalLanguageCriteriaSessionComponent implements OnInit, OnDestroy {

    @Input() targetType: string = 'CUSTOMER';
    @Input() campaignId: number;
    @Input() sessionTimeoutMinutes: number = 30;

    @Output() criteriaApplied = new EventEmitter<{ sessionId: number; criteriaJson: string }>();
    @Output() sessionFinalized = new EventEmitter<NaturalLanguageSessionResponse>();
    @Output() sessionAbandoned = new EventEmitter<number>();

    query: string = '';
    refinementInput: string = '';
    processing: boolean = false;
    sessionResponse: NaturalLanguageSessionResponse = null;
    errorMessage: string = '';
    showExamples: boolean = false;

    clarificationResponses: { [key: number]: string } = {};
    selectedOptions: { [key: number]: number } = {};

    exampleQueries: string[] = [
        'Kunden in Bayern mit Verträgen, die in 30 Tagen ablaufen',
        'Aktive Kunden, die in den letzten 60 Tagen gekauft haben',
        'Hochwertige Verträge mit mehr als 2 Produkten',
        'Inaktive Kunden, die sich seit 90 Tagen nicht angemeldet haben',
        'Premium-Kunden, die zur Verlängerung berechtigt sind'
    ];

    private subscriptions: Subscription[] = [];

    constructor(private sessionService: NaturalLanguageCriteriaSessionService) {
    }

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    startSession(): void {
        if (!this.query.trim()) {
            this.errorMessage = 'Bitte geben Sie eine Beschreibung Ihrer Zielgruppe ein';
            return;
        }

        this.processing = true;
        this.errorMessage = '';

        const request: NaturalLanguageSessionStartRequest = {
            query: this.query,
            targetType: this.targetType,
            campaignId: this.campaignId,
            sessionTimeoutMinutes: this.sessionTimeoutMinutes
        };

        const sub = this.sessionService.startSession(request).subscribe({
            next: (response) => {
                this.sessionResponse = response;
                this.processing = false;
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Sitzung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
        this.subscriptions.push(sub);
    }

    refineCriteria(): void {
        if (!this.refinementInput.trim() || !this.sessionResponse?.sessionId) {
            return;
        }

        this.processing = true;
        this.errorMessage = '';

        const sub = this.sessionService.refineCriteria({
            sessionId: this.sessionResponse.sessionId,
            refinementInput: this.refinementInput
        }).subscribe({
            next: (response) => {
                this.sessionResponse = response;
                this.refinementInput = '';
                this.processing = false;
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Kriterien konnten nicht verfeinert werden. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
        this.subscriptions.push(sub);
    }

    provideClarification(): void {
        if (!this.sessionResponse?.sessionId) return;

        const hasResponses = Object.keys(this.clarificationResponses).length > 0 ||
                            Object.keys(this.selectedOptions).length > 0;
        if (!hasResponses) {
            this.errorMessage = 'Bitte geben Sie mindestens eine Klärung an';
            return;
        }

        this.processing = true;
        this.errorMessage = '';

        // Combine text responses and option selections
        const responses: { [key: number]: string } = {...this.clarificationResponses};
        for (const [questionId, optionIndex] of Object.entries(this.selectedOptions)) {
            responses[parseInt(questionId)] = optionIndex.toString();
        }

        const sub = this.sessionService.provideClarification({
            sessionId: this.sessionResponse.sessionId,
            responses: responses
        }).subscribe({
            next: (response) => {
                this.sessionResponse = response;
                this.clarificationResponses = {};
                this.selectedOptions = {};
                this.processing = false;
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Klärung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
        this.subscriptions.push(sub);
    }

    finalizeSession(): void {
        if (!this.sessionResponse?.sessionId) return;

        this.processing = true;

        const sub = this.sessionService.finalizeSession(this.sessionResponse.sessionId).subscribe({
            next: (response) => {
                this.sessionResponse = response;
                this.sessionFinalized.emit(response);
                this.processing = false;
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Sitzung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
        this.subscriptions.push(sub);
    }

    applyCriteria(): void {
        if (!this.sessionResponse?.sessionId || !this.campaignId) return;

        this.processing = true;

        const sub = this.sessionService.applyCriteria({
            sessionId: this.sessionResponse.sessionId,
            campaignId: this.campaignId,
            replaceExisting: true
        }).subscribe({
            next: (response) => {
                this.sessionResponse = response;
                this.criteriaApplied.emit({
                    sessionId: response.sessionId,
                    criteriaJson: response.currentCriteriaJson
                });
                this.processing = false;
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Kriterien konnten nicht angewendet werden. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
        this.subscriptions.push(sub);
    }

    abandonSession(): void {
        if (!this.sessionResponse?.sessionId) return;

        const sub = this.sessionService.abandonSession(this.sessionResponse.sessionId).subscribe({
            next: () => {
                const sessionId = this.sessionResponse.sessionId;
                this.sessionAbandoned.emit(sessionId);
                this.reset();
            },
            error: (error) => {
                this.errorMessage = error.error?.message || 'Sitzung konnte nicht abgebrochen werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    selectOption(question: ClarificationQuestion, optionIndex: number): void {
        this.selectedOptions[question.questionId] = optionIndex;
        delete this.clarificationResponses[question.questionId];
    }

    setCustomResponse(question: ClarificationQuestion, response: string): void {
        if (response.trim()) {
            this.clarificationResponses[question.questionId] = response;
            delete this.selectedOptions[question.questionId];
        }
    }

    useExample(example: string): void {
        this.query = example;
        this.showExamples = false;
    }

    reset(): void {
        this.query = '';
        this.refinementInput = '';
        this.sessionResponse = null;
        this.errorMessage = '';
        this.clarificationResponses = {};
        this.selectedOptions = {};
    }

    getConfidenceColor(score: number): string {
        if (score >= 0.8) return 'text-green-600';
        if (score >= 0.6) return 'text-yellow-600';
        return 'text-red-600';
    }

    getConfidenceLabel(score: number): string {
        if (score >= 0.8) return 'Hohe Konfidenz';
        if (score >= 0.6) return 'Mittlere Konfidenz';
        return 'Niedrige Konfidenz';
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'ACTIVE':
                return 'text-blue-600';
            case 'AWAITING_CLARIFICATION':
                return 'text-yellow-600';
            case 'CRITERIA_FINALIZED':
                return 'text-green-600';
            case 'APPLIED':
                return 'text-green-800';
            case 'ABANDONED':
            case 'EXPIRED':
                return 'text-gray-500';
            default:
                return '';
        }
    }

    getMessageIcon(message: ConversationMessage): string {
        if (message.role === 'USER') {
            return 'fa-solid fa-user';
        }
        return 'fa-solid fa-robot';
    }

    get isSessionActive(): boolean {
        return this.sessionResponse?.status === 'ACTIVE' ||
               this.sessionResponse?.status === 'AWAITING_CLARIFICATION';
    }

    get hasPendingQuestions(): boolean {
        return this.sessionResponse?.pendingQuestions?.length > 0;
    }

    get canFinalize(): boolean {
        return this.sessionResponse?.isReadyToApply &&
               !this.hasPendingQuestions &&
               this.sessionResponse?.status === 'ACTIVE';
    }

    get canApply(): boolean {
        return this.sessionResponse?.status === 'CRITERIA_FINALIZED';
    }

    formatStatus(status: string): string {
        return status?.toLowerCase().replace(/_/g, ' ') || '';
    }
}
